/**
 * IdentityEditor — modal for an Orchist to edit their public identity.
 *
 * Phase D.5 of the Change Doctrine (sacred/change-doctrine.md):
 * "Recognition is lenient. The Orchist owns their public face. Mode and
 * identity strings can change anytime, no friction."
 *
 * Per JL feedback 2026-04-26:
 *   "Save the names, but not apply. It's the choice of the Orchist to
 *    choose whenever s/he wants. Alias and Named should have different
 *    values saved on db... but neither of them should lead to final
 *    application to the public."
 *
 * The Orchist now stores TWO independent identity strings:
 *   - namedIdentity   (their real name / chosen handle)
 *   - aliasedIdentity (their pseudonym)
 * Both persist regardless of which is currently public. The
 * publicIdentityMode picker selects which face is shown to The Chain
 * (or GENERIC for neither).
 *
 * Two-step flow:
 *   Step 1 — editing:    fill in either or both fields + pick which is public
 *   Step 2 — confirming: review the full diff (mode + each field) before save
 *   Step 3 — apply:      RPC fires, modal closes, refreshContact runs
 *
 * Validation: cannot apply NAMED mode without a stored name; cannot apply
 * ALIASED mode without a stored alias. The form surfaces these locally
 * before submit; the RPC enforces server-side too.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { OrchistIdentityMode } from '@orchist/types';

export interface IdentityEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'editing' | 'confirming';

export function IdentityEditor({ isOpen, onClose }: IdentityEditorProps) {
  const { contact, refreshContact } = useAuth();

  const [step, setStep] = useState<Step>('editing');
  const [mode, setMode] = useState<OrchistIdentityMode>('NAMED');
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset to current values whenever modal (re)opens or contact updates
  useEffect(() => {
    if (isOpen && contact) {
      setStep('editing');
      setMode(contact.publicIdentityMode ?? 'NAMED');
      setName(contact.namedIdentity ?? '');
      setAlias(contact.aliasedIdentity ?? '');
      setSubmitState('idle');
      setErrorMessage(null);
    }
  }, [isOpen, contact]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentMode = contact?.publicIdentityMode ?? null;
  const currentName = contact?.namedIdentity ?? null;
  const currentAlias = contact?.aliasedIdentity ?? null;
  const proposedName = name.trim() === '' ? null : name.trim();
  const proposedAlias = alias.trim() === '' ? null : alias.trim();

  const sameMode = mode === currentMode;
  const sameName = proposedName === currentName;
  const sameAlias = proposedAlias === currentAlias;
  const isNoOp = sameMode && sameName && sameAlias;

  function validate(): string | null {
    if (mode === 'NAMED' && !proposedName) {
      return "You picked Named as your public face, but there's no stored name. Type a name in the Named field, or pick another mode.";
    }
    if (mode === 'ALIASED' && !proposedAlias) {
      return "You picked Aliased as your public face, but there's no stored alias. Type an alias in the Aliased field, or pick another mode.";
    }
    return null;
  }

  function handleReview(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    if (isNoOp) {
      onClose();
      return;
    }
    setErrorMessage(null);
    setStep('confirming');
  }

  async function handleConfirm() {
    if (submitState === 'submitting') return;
    setSubmitState('submitting');
    setErrorMessage(null);
    try {
      const { error } = await supabase.schema('core').rpc('update_identity', {
        p_mode: mode,
        // For each field: pass empty string ('') to clear if user wiped it,
        // or the new value if they typed one. We always pass a value (not
        // null) so the RPC knows we're explicitly setting these fields.
        p_name: name.trim(),
        p_alias: alias.trim(),
      });
      if (error) {
        throw new Error(error.message ?? 'Save failed.');
      }
      if (refreshContact) {
        await refreshContact();
      }
      onClose();
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error.');
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close identity editor"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-lg bg-paper border border-ink/20 shadow-xl p-8 space-y-7 max-h-[90vh] overflow-y-auto">
        {step === 'editing' ? (
          <EditingStep
            mode={mode}
            name={name}
            alias={alias}
            currentMode={currentMode}
            onModeChange={setMode}
            onNameChange={setName}
            onAliasChange={setAlias}
            onCancel={onClose}
            onReview={handleReview}
            errorMessage={errorMessage}
          />
        ) : (
          <ConfirmingStep
            currentMode={currentMode}
            currentName={currentName}
            currentAlias={currentAlias}
            proposedMode={mode}
            proposedName={proposedName}
            proposedAlias={proposedAlias}
            onBack={() => setStep('editing')}
            onConfirm={handleConfirm}
            submitting={submitState === 'submitting'}
            errorMessage={errorMessage}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — editing form
// ---------------------------------------------------------------------------

function EditingStep({
  mode,
  name,
  alias,
  currentMode,
  onModeChange,
  onNameChange,
  onAliasChange,
  onCancel,
  onReview,
  errorMessage,
}: {
  mode: OrchistIdentityMode;
  name: string;
  alias: string;
  currentMode: OrchistIdentityMode | null;
  onModeChange: (m: OrchistIdentityMode) => void;
  onNameChange: (s: string) => void;
  onAliasChange: (s: string) => void;
  onCancel: () => void;
  onReview: (e: FormEvent<HTMLFormElement>) => void;
  errorMessage: string | null;
}) {
  return (
    <>
      <div className="text-center space-y-2">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
          Renew the recognition
        </p>
        <h2 className="display text-3xl font-medium leading-tight">
          <span className="italic text-accent">Edit your identity.</span>
        </h2>
        <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
          Both names persist in your record. Choose at any time which one
          The Chain sees — or none at all.
        </p>
      </div>

      <form onSubmit={onReview} className="space-y-7">
        {/* Two stored fields, always visible */}
        <div className="space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="ident-name"
              className="block text-xs tracking-wide uppercase text-ink/60"
            >
              Your name{' '}
              <span className="lowercase italic tracking-normal text-ink/40">
                (real name or chosen handle)
              </span>
            </label>
            <input
              id="ident-name"
              type="text"
              placeholder="empty"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={120}
              className="w-full px-5 py-3 border border-ink/20 bg-paper/50 text-base font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="ident-alias"
              className="block text-xs tracking-wide uppercase text-ink/60"
            >
              Your alias{' '}
              <span className="lowercase italic tracking-normal text-ink/40">
                (pseudonym crafted for your Orchistness)
              </span>
            </label>
            <input
              id="ident-alias"
              type="text"
              placeholder="empty"
              value={alias}
              onChange={(e) => onAliasChange(e.target.value)}
              maxLength={120}
              className="w-full px-5 py-3 border border-ink/20 bg-paper/50 text-base font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors"
            />
          </div>
        </div>

        {/* Mode picker — choose which face is public */}
        <fieldset className="space-y-3 border-t border-ink/10 pt-6">
          <legend className="text-sm tracking-wide uppercase text-ink/60 mb-2">
            Currently showing as
          </legend>

          <ModeOption
            value="NAMED"
            label="Named"
            preview={name.trim() || <em className="text-ink/35">no name yet</em>}
            current={mode}
            wasCurrent={currentMode === 'NAMED'}
            onPick={onModeChange}
          />
          <ModeOption
            value="ALIASED"
            label="Aliased"
            preview={alias.trim() || <em className="text-ink/35">no alias yet</em>}
            current={mode}
            wasCurrent={currentMode === 'ALIASED'}
            onPick={onModeChange}
          />
          <ModeOption
            value="GENERIC"
            label="Generic"
            preview={<em className="text-ink/55">Orchist &middot; date &middot; glyph</em>}
            current={mode}
            wasCurrent={currentMode === 'GENERIC'}
            onPick={onModeChange}
          />
        </fieldset>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors"
          >
            Review &rarr;
          </button>
        </div>

        {errorMessage && (
          <p className="text-sm text-destructive text-center" role="alert">
            {errorMessage}
          </p>
        )}

        <p className="text-[0.7rem] text-ink/40 italic font-display text-center pt-1 leading-relaxed">
          Editing a field never auto-applies. The Orchist chooses which face
          is public.
        </p>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — confirming review
// ---------------------------------------------------------------------------

function ConfirmingStep({
  currentMode,
  currentName,
  currentAlias,
  proposedMode,
  proposedName,
  proposedAlias,
  onBack,
  onConfirm,
  submitting,
  errorMessage,
}: {
  currentMode: OrchistIdentityMode | null;
  currentName: string | null;
  currentAlias: string | null;
  proposedMode: OrchistIdentityMode;
  proposedName: string | null;
  proposedAlias: string | null;
  onBack: () => void;
  onConfirm: () => void;
  submitting: boolean;
  errorMessage: string | null;
}) {
  const modeChanged = currentMode !== proposedMode;
  const nameChanged = currentName !== proposedName;
  const aliasChanged = currentAlias !== proposedAlias;

  return (
    <>
      <div className="text-center space-y-2">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
          Confirm the change
        </p>
        <h2 className="display text-3xl font-medium leading-tight">
          <span className="italic text-accent">A deliberate gesture.</span>
        </h2>
        <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
          The Line will record the moment. The audit trail preserves the
          path. Confirm only what you mean.
        </p>
      </div>

      <div className="space-y-5">
        {nameChanged && (
          <DiffRow
            label="Stored name"
            from={currentName ?? <em className="text-ink/35">empty</em>}
            to={proposedName ?? <em className="text-ink/55">cleared</em>}
          />
        )}
        {aliasChanged && (
          <DiffRow
            label="Stored alias"
            from={currentAlias ?? <em className="text-ink/35">empty</em>}
            to={proposedAlias ?? <em className="text-ink/55">cleared</em>}
          />
        )}
        {modeChanged && (
          <DiffRow
            label="Public face"
            from={currentMode ?? '—'}
            to={proposedMode}
          />
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
        >
          &larr; Go back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Recording...' : 'Confirm'}
        </button>
      </div>

      {errorMessage && (
        <p className="text-sm text-destructive text-center" role="alert">
          {errorMessage}
        </p>
      )}

      <p className="text-[0.7rem] text-ink/40 italic font-display text-center pt-1">
        The DB preserves your trail. The Line shows what you are now.
      </p>
    </>
  );
}

function DiffRow({
  label,
  from,
  to,
}: {
  label: string;
  from: React.ReactNode;
  to: React.ReactNode;
}) {
  return (
    <div className="border-l-2 border-accent pl-5 py-1 space-y-1">
      <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/45">
        {label}
      </p>
      <p className="display text-sm text-ink/55 line-through decoration-ink/30">
        {from}
      </p>
      <p className="display text-base text-ink">
        <span className="text-accent">→</span> {to}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode option — shows live preview of what's stored under each mode
// ---------------------------------------------------------------------------

function ModeOption({
  value,
  label,
  preview,
  current,
  wasCurrent,
  onPick,
}: {
  value: OrchistIdentityMode;
  label: string;
  preview: React.ReactNode;
  current: OrchistIdentityMode;
  wasCurrent: boolean;
  onPick: (v: OrchistIdentityMode) => void;
}) {
  const active = current === value;
  return (
    <label
      className={`block border p-4 cursor-pointer transition-colors ${
        active ? 'border-ink/60 bg-paper/80' : 'border-ink/15 hover:border-ink/40'
      }`}
    >
      <input
        type="radio"
        name="identity-edit-mode"
        value={value}
        checked={active}
        onChange={() => onPick(value)}
        className="sr-only"
      />
      <div className="flex items-baseline justify-between gap-3">
        <span className="display text-base font-medium">
          {label}
          {wasCurrent && (
            <span className="ml-2 text-[0.6rem] tracking-[0.2em] uppercase text-ink/40 font-sans">
              · current
            </span>
          )}
        </span>
        <span className="text-xs text-ink/55 italic font-display truncate max-w-[60%] text-right">
          {preview}
        </span>
      </div>
    </label>
  );
}
